import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as FinalizeIcon,
  Timer as TimerIcon,
  EmojiEvents as TrophyIcon,
  DirectionsCar as CarIcon,
  PlayArrow as StartIcon,
  People as PeopleIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { timeTrialService } from '../../services/timeTrials';
import gulagService from '../../services/gulag';
import { trackmaniaTimeTrialService } from '../../services/trackmaniaTimeTrials';
import DriverMappingDialog from './DriverMappingDialog';
import TrackmaniaPlayerMappingDialog from './TrackmaniaPlayerMappingDialog';

// Helper function to format date for datetime-local input
const formatDateTimeLocal = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const TimeTrialManagement = () => {
  const [timeTrials, setTimeTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [selectedTrialForMapping, setSelectedTrialForMapping] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    game_id: '',
    start_time: formatDateTimeLocal(new Date()),
    end_time: formatDateTimeLocal(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 1 week from now
    map_uids: [], // For Trackmania
    player_mappings: {} // For Trackmania: PyPlanet login -> GGWP user_id
  });
  
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [timeTrialGames, setTimeTrialGames] = useState([]);
  const [trackmaniaPlayerMappingDialogOpen, setTrackmaniaPlayerMappingDialogOpen] = useState(false);
  const [playerMappings, setPlayerMappings] = useState([]);
  
  useEffect(() => {
    loadTimeTrials();
    loadTimeTrialGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const loadTimeTrials = async () => {
    setLoading(true);
    setError(null);
    
    const result = await timeTrialService.getTimeTrials();
    
    if (result.success) {
      setTimeTrials(result.data);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };
  
  const loadTimeTrialGames = async () => {
    const result = await gulagService.getGames('active', true);
    
    if (result.success) {
      setTimeTrialGames(result.data);
      // Set first game as default if available and no game selected
      if (result.data.length > 0 && !formData.game_id) {
        setFormData(prev => ({ ...prev, game_id: result.data[0].id }));
      }
    } else {
      setError('Failed to load time trial games');
    }
  };
  
  const handleCreate = async () => {
    const result = await timeTrialService.createTimeTrial(formData);
    
    if (result.success) {
      setSuccess('Time trial created successfully!');
      setCreateDialogOpen(false);
      resetForm();
      loadTimeTrials();
    } else {
      setError(result.error);
    }
  };
  
  const handleUpdate = async () => {
    if (!selectedTrial) return;
    
    const result = await timeTrialService.updateTimeTrial(selectedTrial.id, formData);
    
    if (result.success) {
      setSuccess('Time trial updated successfully!');
      setEditDialogOpen(false);
      resetForm();
      loadTimeTrials();
    } else {
      setError(result.error);
    }
  };
  
  const handleStart = async (trialId) => {
    const result = await timeTrialService.startTimeTrial(trialId);
    
    if (result.success) {
      setSuccess('Time trial started successfully!');
      loadTimeTrials();
    } else {
      setError(result.error);
    }
  };
  
  const handleCalculateTrackmaniaRankings = async (trialId) => {
    setError(null);
    // Use sync-and-calculate to ensure fresh data from PyPlanet
    const result = await trackmaniaTimeTrialService.syncAndCalculateRankings(trialId);
    
    if (result.success) {
      const recordsSynced = result.data?.sync_stats?.records_synced || 0;
      const mapsCalculated = result.data?.maps_calculated || 0;
      const totalMaps = result.data?.total_maps || 0;
      setSuccess(
        `Synced ${recordsSynced} records from PyPlanet and calculated ${mapsCalculated}/${totalMaps} maps!`
      );
      loadTimeTrials();
    } else {
      setError(result.error || 'Failed to sync and calculate rankings');
    }
  };
  
  const handleFinalize = async () => {
    if (!selectedTrial) return;
    
    const result = await timeTrialService.finalizeTimeTrial(selectedTrial.id);
    
    if (result.success) {
      const awardedCount = result.data.points_awarded?.length || 0;
      const skippedCount = result.data.skipped_drivers?.length || 0;
      
      let message = `Time trial finalized! Awarded ${awardedCount} driver(s) Gulag points.`;
      if (skippedCount > 0) {
        message += ` ${skippedCount} driver(s) were skipped (not mapped).`;
      }
      
      setSuccess(message);
      setFinalizeDialogOpen(false);
      setSelectedTrial(null);
      loadTimeTrials();
    } else {
      setError(result.error);
    }
  };
  
  const openDeleteDialog = (trial) => {
    setSelectedTrial(trial);
    setDeleteDialogOpen(true);
  };
  
  const openMappingDialog = (trial) => {
    setSelectedTrialForMapping(trial);
    setMappingDialogOpen(true);
  };
  
  const handleDelete = async () => {
    if (!selectedTrial) return;
    
    const result = await timeTrialService.deleteTimeTrial(selectedTrial.id);
    
    if (result.success) {
      setSuccess(`Time trial "${selectedTrial.name}" deleted successfully!`);
      setDeleteDialogOpen(false);
      setSelectedTrial(null);
      loadTimeTrials();
    } else {
      setError(result.error);
    }
  };
  
  const openEditDialog = (trial) => {
    setSelectedTrial(trial);
    
    // Convert player_mappings dict to array format for UI
    const mappingsArray = trial.player_mappings 
      ? Object.entries(trial.player_mappings).map(([pyplanet_login, ggwp_user_id]) => ({
          pyplanet_login,
          ggwp_user_id
        }))
      : [];
    
    setFormData({
      name: trial.name,
      description: trial.description,
      game_id: trial.game_id,
      start_time: formatDateTimeLocal(trial.start_time),
      end_time: formatDateTimeLocal(trial.end_time),
      map_uids: trial.map_uids || [],
      player_mappings: trial.player_mappings || {}
    });
    setPlayerMappings(mappingsArray);
    setEditDialogOpen(true);
  };
  
  const openFinalizeDialog = (trial) => {
    setSelectedTrial(trial);
    setFinalizeDialogOpen(true);
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      game_id: timeTrialGames.length > 0 ? timeTrialGames[0].id : '',
      start_time: formatDateTimeLocal(new Date()),
      end_time: formatDateTimeLocal(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
      map_uids: [],
      player_mappings: {}
    });
    setPlayerMappings([]);
    setSelectedTrial(null);
  };
  
  // Helper function to check if selected game is Trackmania
  const isTrackmaniaSelected = () => {
    const selectedGame = timeTrialGames.find(g => g.id === formData.game_id);
    return selectedGame?.name?.toLowerCase().includes('trackmania');
  };
  
  // Helper function to handle map UIDs input
  const handleMapUidsChange = (e) => {
    const value = e.target.value;
    // Split by comma, trim whitespace, filter empty strings
    const uids = value.split(',').map(uid => uid.trim()).filter(uid => uid.length > 0);
    setFormData({ ...formData, map_uids: uids });
  };
  
  // Helper function to save player mappings
  const handleSavePlayerMappings = (mappings) => {
    const mappingDict = {};
    mappings.forEach(mapping => {
      if (mapping.pyplanet_login && mapping.ggwp_user_id) {
        mappingDict[mapping.pyplanet_login] = mapping.ggwp_user_id;
      }
    });
    setFormData({ ...formData, player_mappings: mappingDict });
    setPlayerMappings(mappings);
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'info';
      case 'active':
        return 'success';
      case 'finalized':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TimerIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Time Trial Management
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Time Trial
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total Trials
                </Typography>
                <Typography variant="h4" color="primary">
                  {timeTrials.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Active
                </Typography>
                <Typography variant="h4" color="success.main">
                  {timeTrials.filter(t => t.status === 'active').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Upcoming
                </Typography>
                <Typography variant="h4" color="info.main">
                  {timeTrials.filter(t => t.status === 'upcoming').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Finalized
                </Typography>
                <Typography variant="h4" color="text.secondary">
                  {timeTrials.filter(t => t.status === 'finalized').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Time Trials Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Event</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Start Time</strong></TableCell>
                  <TableCell><strong>End Time</strong></TableCell>
                  <TableCell><strong>Tracks</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeTrials.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No time trials yet. Create one to get started!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  timeTrials.map((trial) => (
                    <TableRow key={trial.id || trial._id}>
                      <TableCell>{trial.name}</TableCell>
                      <TableCell>{trial.event_name || 'Unknown Event'}</TableCell>
                      <TableCell>
                        <Chip
                          icon={<CarIcon />}
                          label={trial.game_name || 'Unknown Game'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={trial.status}
                          color={getStatusColor(trial.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(trial.start_time).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {new Date(trial.end_time).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${trial.tracks.length} tracks`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(trial)}
                          disabled={trial.status === 'finalized'}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        {trial.status === 'upcoming' && (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleStart(trial.id)}
                            title="Start Time Trial"
                          >
                            <StartIcon />
                          </IconButton>
                        )}
                        {trial.status === 'active' && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openFinalizeDialog(trial)}
                            title="Finalize and Award Points"
                          >
                            <FinalizeIcon />
                          </IconButton>
                        )}
                        {trial.game_name?.toLowerCase().includes('trackmania') && trial.status !== 'finalized' && (
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleCalculateTrackmaniaRankings(trial.id)}
                            title="Calculate Trackmania Rankings"
                          >
                            <CalculateIcon />
                          </IconButton>
                        )}
                        {trial.game_name?.toLowerCase().includes('acc') && (
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => openMappingDialog(trial)}
                            title="Manage ACC Participants"
                            disabled={trial.status === 'finalized'}
                          >
                            <PeopleIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog(trial)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Time Trial</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Alert severity="info">
                This time trial will be created for the currently active Gulag-enabled event. 
                You can configure Gulag events in Admin → Gulag → Event Configuration.
              </Alert>
              
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />
              
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
              
              <TextField
                label="Game"
                select
                value={formData.game_id}
                onChange={(e) => setFormData({ ...formData, game_id: e.target.value })}
                fullWidth
                required
                helperText={timeTrialGames.length === 0 ? "No time trial games available. Please mark a game as time trial compatible in Game Management." : ""}
              >
                {timeTrialGames.map((game) => (
                  <MenuItem key={game.id} value={game.id}>
                    {game.name}
                  </MenuItem>
                ))}
              </TextField>
              
              {/* Trackmania-specific configuration */}
              {isTrackmaniaSelected() && (
                <>
                  <TextField
                    label="Map UIDs (comma-separated)"
                    value={(formData.map_uids || []).join(', ')}
                    onChange={handleMapUidsChange}
                    fullWidth
                    multiline
                    rows={3}
                    helperText="Enter Trackmania map UIDs separated by commas (e.g., ABC123XYZ, DEF456ABC)"
                  />
                  
                  <Button
                    variant="outlined"
                    onClick={() => setTrackmaniaPlayerMappingDialogOpen(true)}
                    startIcon={<PeopleIcon />}
                    fullWidth
                  >
                    Configure Player Mappings ({playerMappings.length} mapped)
                  </Button>
                </>
              )}
              
              <TextField
                label="Start Time"
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              
              <TextField
                label="End Time"
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>
        
        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Time Trial</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
              />
              
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
              
              {/* Trackmania-specific configuration */}
              {isTrackmaniaSelected() && (
                <>
                  <TextField
                    label="Map UIDs (comma-separated)"
                    value={(formData.map_uids || []).join(', ')}
                    onChange={handleMapUidsChange}
                    fullWidth
                    multiline
                    rows={3}
                    helperText="Enter Trackmania map UIDs separated by commas (e.g., ABC123XYZ, DEF456ABC)"
                  />
                  
                  <Button
                    variant="outlined"
                    onClick={() => setTrackmaniaPlayerMappingDialogOpen(true)}
                    startIcon={<PeopleIcon />}
                    fullWidth
                  >
                    Configure Player Mappings ({playerMappings.length} mapped)
                  </Button>
                </>
              )}
              
              <TextField
                label="Start Time"
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              
              <TextField
                label="End Time"
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} variant="contained">Update</Button>
          </DialogActions>
        </Dialog>
        
        {/* Finalize Dialog */}
        <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)}>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrophyIcon color="primary" />
              Finalize Time Trial
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action will award Gulag points to all participants based on their rankings. This cannot be undone.
            </Alert>
            <Typography variant="body1">
              Are you sure you want to finalize "{selectedTrial?.name}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Points will be awarded based on the TimeBasedPointsConfig and added to the event's Gulag rankings.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFinalizeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleFinalize} variant="contained" color="primary">
              Finalize & Award Points
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteIcon color="error" />
              Delete Time Trial
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              This action will permanently delete this time trial. This cannot be undone.
            </Alert>
            <Typography variant="body1">
              Are you sure you want to delete "{selectedTrial?.name}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              All associated data including standings and track information will be removed.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Delete Time Trial
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Driver Mapping Dialog (for ACC) */}
        <DriverMappingDialog
          open={mappingDialogOpen}
          onClose={() => {
            setMappingDialogOpen(false);
            setSelectedTrialForMapping(null);
          }}
          trial={selectedTrialForMapping}
        />
        
        {/* Trackmania Player Mapping Dialog */}
        <TrackmaniaPlayerMappingDialog
          open={trackmaniaPlayerMappingDialogOpen}
          onClose={() => setTrackmaniaPlayerMappingDialogOpen(false)}
          mappings={playerMappings}
          onSave={handleSavePlayerMappings}
        />
      </Box>
  );
};

export default TimeTrialManagement;

